import { getAdminT } from "../i18n/getAdminT";
import { getAdminUiLocale } from "../i18n/getAdminUiLocale";
import type { Locale } from "../localization/constants";

export type EventStatus =
  | "provisioning"
  | "provisioning_failed"
  | "running"
  | "archived"
  | "deleting";

export const EVENT_OPERATIONAL_STATUSES: readonly EventStatus[] = ["running"];

export const EVENT_MANAGER_STATUSES: readonly EventStatus[] = ["running"];

export const EVENT_ARCHIVABLE_STATUSES: readonly EventStatus[] = [
  "running",
  "provisioning_failed",
];

export const EVENT_STATUS_COLOR: Record<EventStatus, string> = {
  provisioning: "grayDark",
  provisioning_failed: "redDark",
  running: "greenLight",
  archived: "purpleDark",
  deleting: "yellowLight",
};

export const isEventOperational = (status: EventStatus | undefined): boolean =>
  status != null && EVENT_OPERATIONAL_STATUSES.includes(status);

export const isEventManagerAccessible = (
  status: EventStatus | undefined
): boolean => status != null && EVENT_MANAGER_STATUSES.includes(status);

export const buildEventStatusEnumValues = (
  locale: Locale = getAdminUiLocale()
): Record<EventStatus, string> => {
  const t = getAdminT(locale);
  return {
    provisioning: t("eventStatus.provisioning"),
    provisioning_failed: t("eventStatus.provisioning_failed"),
    running: t("eventStatus.running"),
    archived: t("eventStatus.archived"),
    deleting: t("eventStatus.deleting"),
  };
};

export const eventStatusLabel = (
  status: EventStatus | undefined,
  locale: Locale = getAdminUiLocale()
): string => {
  const labels = buildEventStatusEnumValues(locale);
  if (!status) {
    return labels.provisioning;
  }
  return labels[status];
};

export const eventStatusDenialMessage = (
  status: EventStatus | undefined,
  provisioningError?: string,
  locale: Locale = getAdminUiLocale()
): string => {
  const t = getAdminT(locale);
  if (status === "provisioning_failed") {
    const detail = provisioningError?.trim();
    return detail
      ? t("eventStatus.denialProvisioningFailedWithDetail", { detail })
      : t("eventStatus.denialProvisioningFailed");
  }
  if (status === "provisioning") {
    return t("eventStatus.denialProvisioning");
  }
  if (status === "archived") {
    return t("eventStatus.denialArchived");
  }
  return t("eventStatus.denialGeneric");
};
